import React from 'react';
import PropTypes from 'prop-types';
import { AgencyPage } from '../../../components/Views/AgencyPage';
import Layout from '../../../components/Layout/Layout';
import { getStaticData } from '../../../utils/helpers/getStaticData';
import { getAgencySeoData } from '../../../utils/helpers/seo';
import { getSelectedAgencyQuery } from '../../../api/queries';
import { normalizePath } from '../../../utils/helpers';
import { withBugsnagGetServerSideProps } from '../../../utils/withBugsnag';
import { loadAgencyMockData } from '../../../utils/helpers/loadAgencyMockData';
import { extractApolloState } from '../../../utils/helpers/extractApolloState';
import { prepareSSRContext } from '../../../utils/helpers/ssrContext';
import { shouldUseMock } from '../../../utils/helpers/shouldUseMock';
import {
  parseAgencySegments,
  isValidAgencyUrl,
  getCategorySlug,
  getCanonicalAgencyPath,
  getEmptyResultsRedirectDestination
} from '../../../utils/helpers/agencyPage';
import { getLocaleOnSSR } from '../../../utils/helpers/getLocaleOnSSR';

const Agency = ({ agencySlug, hasError, currentLang, initialFilter }) => (
  <AgencyPage
    agencySlug={agencySlug}
    hasError={hasError}
    currentLang={currentLang}
    initialFilter={initialFilter}
  />
);

export const getServerSideProps = withBugsnagGetServerSideProps(
  async ctx => {
    const { res, req, params, query } = ctx;
    const {
      apolloClient,
      apolloOptions,
      currentLang,
      isSSRWebView,
      abTests,
      hasAccessToken
    } = prepareSSRContext(ctx);

    const slug = params.agency;
    const segments = Array.isArray(params.segments) ? params.segments : [];

    const asPath = `/agentlikler/${slug}${
      segments.length ? `/${segments.join('/')}` : ''
    }`;
    const newAsPath = normalizePath(asPath);

    if (shouldUseMock(query)) {
      try {
        const agency = await loadAgencyMockData(apolloClient, slug);
        await getStaticData(apolloClient);

        return extractApolloState(apolloClient, {
          props: {
            seoData: getAgencySeoData(
              agency?.name,
              currentLang,
              newAsPath,
              segments,
              req
            ),
            agencySlug: slug,
            currentLang
          }
        });
      } catch (error) {
        console.error('Error loading mock data:', error);
      }
    }

    res.setHeader(
      'Cache-Control',
      'public, max-age=60, s-maxage=60, stale-while-revalidate=86400'
    );

    try {
      const [agencyRes] = await Promise.all([
        apolloClient.query({
          query: getSelectedAgencyQuery,
          variables: { slug },
          errorPolicy: 'all'
        }),
        getStaticData(apolloClient)
      ]);

      const agency = agencyRes?.data?.agency ?? null;
      const hasGraphQLErrors = Boolean(agencyRes?.errors?.length);

      if (!agency && hasGraphQLErrors) {
        return {
          props: {
            seoData: null,
            agencySlug: slug,
            hasError: true,
            apolloOptions,
            currentLang,
            isSSRWebView,
            abTests,
            hasAccessToken
          }
        };
      }

      if (!agency && !hasGraphQLErrors) {
        return { notFound: true };
      }

      const { dealType, category } = parseAgencySegments(
        segments,
        agency.itemCategories
      );

      if (!isValidAgencyUrl(segments, category)) {
        return {
          redirect: { destination: `/agentlikler/${slug}`, permanent: false }
        };
      }

      const categorySlug = getCategorySlug(category, agency.itemCategories);

      const canonicalRedirect = getCanonicalAgencyPath(
        slug,
        segments,
        dealType,
        categorySlug
      );

      if (canonicalRedirect) {
        return {
          redirect: { destination: canonicalRedirect, permanent: false }
        };
      }

      if (segments.length) {
        const emptyRedirect = await getEmptyResultsRedirectDestination({
          apolloClient,
          agencyId: agency.id,
          dealType,
          category,
          categorySlug,
          slug,
          itemCategories: agency.itemCategories
        });

        if (emptyRedirect) {
          return {
            redirect: { destination: emptyRedirect, permanent: false }
          };
        }
      }

      return extractApolloState(apolloClient, {
        props: {
          seoData: getAgencySeoData(
            agency?.name,
            currentLang,
            newAsPath,
            segments,
            req
          ),
          agencySlug: slug,
          hasError: false,
          apolloOptions,
          currentLang,
          isSSRWebView,
          abTests,
          hasAccessToken,
          initialFilter: {
            dealType,
            category
          }
        }
      });
    } catch (error) {
      res.setHeader('Cache-Control', 'no-store');
      throw error;
    }
  },
  'agencies/[agency]/[[...segments]]',
  ({ req }) => ({
    seoData: null,
    agencySlug: '',
    hasError: true,
    currentLang: getLocaleOnSSR(req)
  })
);

Agency.getLayout = (page, pageProps) => (
  <Layout
    isBottomNavigation={false}
    isBanner={false}
    isShowBackButton
    isShowCloseButton={false}
    pageProps={pageProps}
    hideMobileHeader
  >
    {page}
  </Layout>
);

Agency.propTypes = {
  agencySlug: PropTypes.string,
  hasError: PropTypes.bool,
  currentLang: PropTypes.string,
  apolloOptions: PropTypes.object,
  isSSRWebView: PropTypes.bool,
  abTests: PropTypes.object,
  hasAccessToken: PropTypes.bool,
  initialFilter: PropTypes.shape({
    dealType: PropTypes.string,
    category: PropTypes.string
  })
};

export default Agency;
